import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const NBSP = ' ';
/**
 * Le format français d'Intl sépare les milliers par une espace fine insécable (U+202F), presque invisible dans la police de
 * l'application : les chiffres paraissent collés. On la remplace par une espace insécable ordinaire, lisible partout.
 */
const espaces = (s: string): string => s.replace(/[  ]/g, NBSP);

/** Nombre en français avec séparateur de milliers lisible (1 234 567,50). */
export const nombre = (n: number | null | undefined, decimales = 0): string => (n === null || n === undefined ? '—' : espaces(n.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })));

/** Montant en USD, sans décimales par défaut (tableaux de bord), avec décimales si demandé (lignes). */
export const montant = (n: number | null | undefined, decimales = false): string => (n === null || n === undefined ? '—' : `${nombre(n, decimales ? 2 : 0)}${NBSP}USD`);

/** Montant abrégé pour les indicateurs : 180,8 M USD, 697 k USD, 5 700 USD (le montant exact est donné à côté ou en infobulle). */
export const compact = (n: number | null | undefined, unite = 'USD'): string => {
  if (n === null || n === undefined) return '—';
  const a = Math.abs(n);
  const f = (v: number, d: number) => espaces(v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: d }));
  if (a >= 1_000_000) return `${f(n / 1_000_000, 1)}${NBSP}M${unite ? `${NBSP}${unite}` : ''}`;
  if (a >= 10_000) return `${f(n / 1000, 0)}${NBSP}k${unite ? `${NBSP}${unite}` : ''}`;
  return `${f(n, 0)}${unite ? `${NBSP}${unite}` : ''}`;
};

export const pct = (n: number | null | undefined, decimales = 1): string => (n === null || n === undefined ? '—' : `${espaces(n.toLocaleString('fr-FR', { maximumFractionDigits: decimales }))}${NBSP}%`);

export const dateFr = (d: string | Date | null | undefined): string => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export const dateHeureFr = (d: string | Date | null | undefined): string =>
  d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const heures = (h: number | null | undefined): string => (h === null || h === undefined ? '—' : h < 48 ? `${Math.round(h)}${NBSP}h` : `${Math.round((h / 24) * 10) / 10}${NBSP}j`);
